import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * The one button in the design system. See Apps/web/THEME.md.
 *
 * Green fills the PRIMARY action; that never changes. `accent` is the gold
 * secondary — gold may fill only under dark ink (5.91:1; gold with white text
 * is 2.3:1 and reads broken), so it carries a second, lesser call to action,
 * typically on a deep-green or warm band. `danger` is red, for destructive
 * actions only.
 *
 * Flat colours throughout: the restraint doctrine rules out gradients and
 * coloured glow shadows, so depth comes from the shape and one neutral
 * shadow step, and hover is a plain colour change.
 *
 * Any other <button> attribute (aria-*, id, title, name, form, data-*) passes
 * straight through, and a ref reaches the <button> (e.g. as a Popover anchor).
 */
type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'className' | 'onClick' | 'disabled' | 'type'
>;

interface ButtonProps extends NativeButtonProps {
  children: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'cta' | 'accent' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'link';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  type = 'button',
  loading = false,
  ...rest
}, ref) {
  // One focus treatment for every variant — the brand ring from THEME.md §3.
  const baseClasses =
    'inline-flex items-center justify-center rounded-xl font-semibold transition-colors duration-200 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98] min-h-[44px]';

  const variantClasses: Record<Required<ButtonProps>['variant'], string> = {
    // The one solid action per view.
    primary:
      'bg-[#004D40] text-white shadow-brand-xs hover:bg-[#003930] active:bg-[#002A23] ' +
      'dark:bg-[#3FBBA2] dark:text-[#002A23] dark:hover:bg-[#7FD6C3]',
    // THEME.md §2.4c R2 — the RED marketing call to action. Identical paint
    // to `danger` but a separate name on purpose: `danger` means destructive
    // and must keep that meaning, while the board puts red on the thing it
    // wants pressed (both mock-up search buttons are red). Use this for
    // "list your property", "start now", "browse" — never for delete.
    cta:
      'bg-[#DF2F2B] text-white shadow-brand-xs hover:bg-[#B71C1C] active:bg-[#C62828]',
    // THEME.md §2.4 — the gold secondary. Dark ink, never white text.
    accent:
      'bg-[#FFC107] text-[#4A3800] shadow-brand-xs hover:bg-[#FBD35A] active:bg-[#B58A0A] ' +
      'dark:bg-[#FBD35A] dark:text-[#4A3800] dark:hover:bg-[#FBE5A3]',
    // White surface, green hairline on hover — the default for anything
    // that sits next to a primary.
    secondary:
      'bg-white text-[var(--pv-color-text)] border border-[var(--pv-border-soft)] shadow-brand-xs hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-soft)] ' +
      'dark:bg-[#08372F] dark:text-[#E6F3EF] dark:border-white/15 dark:hover:bg-[#0A4A3E]',
    outline:
      'border border-[#004D40] text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] ' +
      'dark:border-[#3FBBA2] dark:text-[#3FBBA2] dark:hover:bg-[#0A4A3E]',
    ghost:
      'text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] dark:text-[#7FD6C3] dark:hover:bg-[#0A4A3E]',
    // The only place red is a fill.
    danger:
      'bg-[#DF2F2B] text-white shadow-brand-xs hover:bg-[#B71C1C] active:bg-[#C62828]',
    link:
      'text-[#1B7566] underline-offset-4 hover:underline hover:text-[#004D40] min-h-0 ' +
      'dark:text-[#7FD6C3] dark:hover:text-[#E6F3EF]',
  };

  const sizeClasses: Record<Required<ButtonProps>['size'], string> = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-2.5 text-base',
    lg: 'px-8 py-3 text-lg',
  };

  // `link` opts out of the button padding so it can sit inline in a sentence.
  // Each variant owns its ring colour (see baseClasses): a green ring vanishes
  // against a gold or red fill.
  const ring =
    variant === 'accent' ? 'focus-visible:ring-[#8C6A0B]'
    : variant === 'danger' || variant === 'cta' ? 'focus-visible:ring-[#C62828]'
    : 'focus-visible:ring-[#004D40]';

  const sizing = variant === 'link' ? 'text-base' : sizeClasses[size];
  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${ring} ${sizing} ${className}`;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
    >
      <span className="inline-flex items-center gap-2">
        {loading && <i className="ri-loader-4-line animate-spin"></i>}
        {children}
      </span>
    </button>
  );
});

export default Button;
